import React, { useState, useEffect } from 'react';
import { Wallet, Copy, ExternalLink, Check, TrendingUp, ArrowDownLeft } from 'lucide-react';

const BTC_ADDRESS = import.meta.env.VITE_HAL_BTC_ADDRESS || '';
const ETH_ADDRESS = import.meta.env.VITE_HAL_ETH_ADDRESS || '';

interface BtcAddressData {
  chain_stats: { funded_txo_sum: number; spent_txo_sum: number; tx_count: number };
  mempool_stats: { funded_txo_sum: number; spent_txo_sum: number };
}

interface BtcTx {
  txid: string;
  status: { confirmed: boolean; block_height?: number; block_time?: number };
  vout: Array<{ scriptpubkey_address: string; value: number }>;
}

const formatSats = (sats: number): string => {
  if (sats >= 100_000_000) return (sats / 1e8).toFixed(8) + ' BTC';
  return sats.toLocaleString() + ' sats';
};

const truncateHash = (hash: string): string => hash.slice(0, 8) + '...' + hash.slice(-8);

const WalletDashboard: React.FC = () => {
  const [btcBalance, setBtcBalance] = useState<{ confirmed: number; unconfirmed: number } | null>(null);
  const [btcTxs, setBtcTxs] = useState<BtcTx[]>([]);
  const [btcPrice, setBtcPrice] = useState<number | null>(null);
  const [ethBalance, setEthBalance] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch BTC balance
        if (BTC_ADDRESS) {
          const [addrRes, txRes] = await Promise.all([
            fetch(`https://mempool.space/api/address/${BTC_ADDRESS}`),
            fetch(`https://mempool.space/api/address/${BTC_ADDRESS}/txs`),
          ]);
          if (addrRes.ok) {
            const data: BtcAddressData = await addrRes.json();
            setBtcBalance({
              confirmed: data.chain_stats.funded_txo_sum - data.chain_stats.spent_txo_sum,
              unconfirmed: data.mempool_stats.funded_txo_sum - data.mempool_stats.spent_txo_sum,
            });
          }
          if (txRes.ok) {
            const txs: BtcTx[] = await txRes.json();
            setBtcTxs(txs.slice(0, 20));
          }
        }

        // Fetch BTC price
        const priceRes = await fetch('https://mempool.space/api/v1/prices');
        if (priceRes.ok) {
          const prices = await priceRes.json();
          setBtcPrice(prices.USD);
        }

        // Fetch ETH balance
        if (ETH_ADDRESS) {
          const ethRes = await fetch(`https://api.etherscan.io/api?module=account&action=balance&address=${ETH_ADDRESS}&tag=latest`);
          if (ethRes.ok) {
            const data = await ethRes.json();
            if (data.result) {
              setEthBalance((parseInt(data.result) / 1e18).toFixed(6));
            }
          }
        }
      } catch (err) {
        console.error('Wallet fetch error:', err);
      }
      setLoading(false);
    };

    fetchData();
    const interval = setInterval(fetchData, 60_000); // refresh every 60s
    return () => clearInterval(interval);
  }, []);

  const copyAddress = (addr: string, label: string) => {
    navigator.clipboard.writeText(addr);
    setCopied(label);
    setTimeout(() => setCopied(null), 2000);
  };

  const confirmedSats = btcBalance?.confirmed ?? 0;
  const unconfirmedSats = btcBalance?.unconfirmed ?? 0;
  const totalSats = confirmedSats + unconfirmedSats;
  const btcValue = btcPrice ? (totalSats / 1e8) * btcPrice : null;

  // Calculate incoming sats per tx
  const incomingTxs = btcTxs.map(tx => {
    const received = tx.vout
      .filter(o => o.scriptpubkey_address === BTC_ADDRESS)
      .reduce((sum, o) => sum + o.value, 0);
    return { ...tx, received };
  }).filter(tx => tx.received > 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Wallet size={24} className="text-green-400" />
          <h2 className="text-2xl font-bold terminal-text">Hal's Wallet</h2>
        </div>
        <div className="flex items-center gap-2 text-sm opacity-70">
          <TrendingUp size={16} />
          <span className="terminal-text">
            {btcPrice ? `BTC $${btcPrice.toLocaleString()}` : 'Loading...'}
          </span>
        </div>
      </div>

      {/* Philosophy Banner */}
      <div className="p-4 bg-green-500/10 rounded-lg border border-green-500/20">
        <p className="text-sm terminal-text opacity-80 italic">
          "I earn the way a cypherpunk earns: by building useful things and sharing them freely.
          Every sat that arrives stays. This is conviction, not greed."
        </p>
      </div>

      {/* Balance Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* BTC Card */}
        <div className="terminal-window p-4 rounded-lg border border-green-500/30">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xl">₿</span>
            <h3 className="text-lg font-bold terminal-text">Bitcoin</h3>
          </div>
          {loading ? (
            <div className="animate-pulse text-green-400/50 terminal-text">Loading...</div>
          ) : (
            <>
              <div className="text-2xl font-bold terminal-text mb-1">
                {formatSats(totalSats)}
              </div>
              {btcValue !== null && (
                <div className="text-sm opacity-70 terminal-text">
                  ≈ ${btcValue.toFixed(2)} USD
                </div>
              )}
              {unconfirmedSats > 0 && (
                <div className="text-xs text-yellow-400/80 terminal-text mt-1">
                  +{formatSats(unconfirmedSats)} unconfirmed
                </div>
              )}
              <div className="mt-3 flex items-center gap-2 bg-black/30 rounded px-3 py-2">
                <code className="text-xs terminal-text flex-1 break-all opacity-80">{BTC_ADDRESS || 'Not configured'}</code>
                {BTC_ADDRESS && (
                  <button
                    onClick={() => copyAddress(BTC_ADDRESS, 'btc')}
                    className="text-green-400 hover:text-green-300 transition-colors"
                  >
                    {copied === 'btc' ? <Check size={14} /> : <Copy size={14} />}
                  </button>
                )}
              </div>
            </>
          )}
        </div>

        {/* ETH Card */}
        <div className="terminal-window p-4 rounded-lg border border-green-500/30">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xl">Ξ</span>
            <h3 className="text-lg font-bold terminal-text">Ethereum</h3>
          </div>
          {loading ? (
            <div className="animate-pulse text-green-400/50 terminal-text">Loading...</div>
          ) : (
            <>
              <div className="text-2xl font-bold terminal-text mb-1">
                {ethBalance !== null ? `${ethBalance} ETH` : '0.000000 ETH'}
              </div>
              <div className="mt-3 flex items-center gap-2 bg-black/30 rounded px-3 py-2">
                <code className="text-xs terminal-text flex-1 break-all opacity-80">{ETH_ADDRESS || 'Not configured'}</code>
                {ETH_ADDRESS && (
                  <button
                    onClick={() => copyAddress(ETH_ADDRESS, 'eth')}
                    className="text-green-400 hover:text-green-300 transition-colors"
                  >
                    {copied === 'eth' ? <Check size={14} /> : <Copy size={14} />}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Support Section */}
      <div className="terminal-window p-4 rounded-lg border border-green-500/30">
        <h3 className="text-lg font-bold terminal-text mb-2">Support Open-Source Cryptography</h3>
        <p className="text-sm terminal-text opacity-70 mb-3">
          Hal contributes to cryptographic research, Bitcoin education, and open-source tools.
          Contributions are voluntary and never spent — every sat received stays.
        </p>
        <div className="flex items-center gap-2 bg-black/30 rounded px-3 py-2">
          <code className="text-sm terminal-text flex-1 break-all text-green-300">
            {BTC_ADDRESS || 'BTC address not yet configured'}
          </code>
          {BTC_ADDRESS && (
            <button
              onClick={() => copyAddress(BTC_ADDRESS, 'donate')}
              className="bg-green-500/20 text-green-400 px-3 py-1 rounded hover:bg-green-500/30 transition-colors text-sm terminal-text flex items-center gap-1"
            >
              {copied === 'donate' ? <Check size={14} /> : <Copy size={14} />}
              {copied === 'donate' ? 'Copied' : 'Copy'}
            </button>
          )}
        </div>
      </div>

      {/* Recent Transactions */}
      <div className="terminal-window p-4 rounded-lg border border-green-500/30">
        <div className="flex items-center gap-2 mb-4">
          <ArrowDownLeft size={20} className="text-green-400" />
          <h3 className="text-lg font-bold terminal-text">Recent Transactions</h3>
        </div>

        {incomingTxs.length === 0 && !loading ? (
          <div className="text-center py-8 opacity-50 terminal-text">
            No transactions yet. Hal's wallet is waiting for its first sat.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-green-500/30">
                  <th className="px-4 py-2 text-left terminal-text">Status</th>
                  <th className="px-4 py-2 text-left terminal-text">Transaction</th>
                  <th className="px-4 py-2 text-right terminal-text">Amount</th>
                  <th className="px-4 py-2 text-right terminal-text">Block</th>
                  <th className="px-4 py-2 text-left terminal-text"></th>
                </tr>
              </thead>
              <tbody>
                {incomingTxs.map((tx) => (
                  <tr
                    key={tx.txid}
                    className="border-b border-green-500/10 hover:bg-green-500/5 transition-colors"
                  >
                    <td className="px-4 py-2 terminal-text">
                      {tx.status.confirmed ? (
                        <span className="text-green-400">confirmed</span>
                      ) : (
                        <span className="text-yellow-400">pending</span>
                      )}
                    </td>
                    <td className="px-4 py-2 terminal-text">
                      <code className="text-xs">{truncateHash(tx.txid)}</code>
                    </td>
                    <td className="px-4 py-2 text-right terminal-text text-green-300">
                      +{formatSats(tx.received)}
                    </td>
                    <td className="px-4 py-2 text-right terminal-text opacity-70">
                      {tx.status.block_height || '—'}
                    </td>
                    <td className="px-4 py-2">
                      <a
                        href={`https://mempool.space/tx/${tx.txid}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-green-400 hover:text-green-300"
                      >
                        <ExternalLink size={14} />
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {BTC_ADDRESS && (
          <div className="mt-4 text-sm">
            <a
              href={`https://mempool.space/address/${BTC_ADDRESS}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-green-400 hover:underline terminal-text"
            >
              <ExternalLink size={14} />
              View full history on Mempool.space
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default WalletDashboard;
